import React, { useEffect, useState } from "react"
import OdontogramSelector from "../modals/OdontogramSelector"
import "../../../shared/styles/system.css"
import { ui } from "../../../shared/styles/uiSystem"

export default function PatientWorkspaceDrawer({
  open,
  tab,
  onClose,
  onOpenTab,
  loadingPatientSnapshot,
  loadingPatientOdontogram,
  loadingTreatmentPlans,
  loadingPatientAlerts,
  patientAlerts,
  patientSnapshot,
  patientOdontogram,
  treatmentPlans,
  selectedAppointment,
  formatDateTime,
  formatMoney,
  planItemStatusLabel,
  creatingOdontogramEntry,
  odontogramForm,
  setOdontogramForm,
  surfaces,
  conditions,
  odontoStatuses,
  onCreateOdontogramEntry,
  onAlertAction,
  onScheduleFromPlanItem
}) {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440
  )

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  if (!open) return null

  const isMobile = viewportWidth < 820
  const patientName =
    patientSnapshot?.patient
      ? `${patientSnapshot.patient.firstName || ""} ${patientSnapshot.patient.lastName || ""}`.trim()
      : patientOdontogram?.patient
        ? `${patientOdontogram.patient.firstName || ""} ${patientOdontogram.patient.lastName || ""}`.trim()
        : selectedAppointment?.patientName || "Paciente"

  return (
    <div
      className="of-drawer of-drawer--right of-drawer--lg"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        background: "rgba(15,23,42,0.18)",
        display: "flex",
        justifyContent: "flex-end",
        backdropFilter: "blur(2px)"
      }}
      onMouseDown={() => onClose?.("backdrop")}
    >
      <div
        className="of-drawer__inner"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "#f8fafc",
          position: "relative",
          zIndex: 2
        }}
      >
        <div
          className="of-drawer__header"
          style={{
            borderBottom: "1px solid #e2e8f0",
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)"
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "flex-start",
              flexWrap: "wrap"
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  display: "inline-flex",
                  padding: "6px 10px",
                  borderRadius: 999,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1d4ed8",
                  fontSize: 10,
                  fontWeight: 900,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  marginBottom: 10
                }}
              >
                Paciente
              </div>

              <h3
                className="of-drawer__title"
                style={{
                  lineHeight: 1.02,
                  fontSize: 26,
                  letterSpacing: "-0.03em",
                  marginBottom: 4
                }}
              >
                {tab === "expediente" ? "Expediente del paciente" : "Odontograma del paciente"}
              </h3>

              <div
                className="of-drawer__subtitle"
                style={{
                  fontSize: 13
                }}
              >
                {patientName}
              </div>
            </div>

            <button
              onClick={onClose}
              className="of-drawer__close"
            >
              Cerrar
            </button>
          </div>
        </div>

        <div
          style={{
            padding: "14px 16px 0",
            display: "flex",
            gap: 8,
            flexWrap: "wrap"
          }}
        >
          <button
            onClick={() => onOpenTab("expediente")}
            style={tab === "expediente" ? ui.primaryButton : ui.secondaryButton}
          >
            Expediente
          </button>

          <button
            onClick={() => onOpenTab("odontograma")}
            style={
              tab === "odontograma"
                ? {
                    ...ui.secondaryButton,
                    background: "#dcfce7",
                    border: "1px solid #bbf7d0",
                    color: "#166534"
                  }
                : ui.secondaryButton
            }
          >
            Odontograma
          </button>
        </div>

        <div
          className="of-drawer__body"
          style={{
            paddingTop: 14,
            display: "grid",
            gap: 14
          }}
        >
          {tab === "expediente" && (
            <>
              {loadingPatientAlerts ? (
                <div
                  style={{
                    ...ui.infoCard,
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    color: "#9a3412",
                    fontWeight: 700,
                    borderLeft: "4px solid #f97316",
                    padding: 12
                  }}
                >
                  Cargando alertas clínicas...
                </div>
              ) : patientAlerts?.length ? (
                <SectionCard
                  title="Alertas clínicas"
                  subtitle="Situaciones que requieren atención rápida"
                >
                  <div style={{ display: "grid", gap: 10 }}>
                    {patientAlerts.map((a, i) => (
                      <div
                        key={`${a.title}-${i}`}
                        style={{
                          ...ui.card,
                          border: "1px solid #fed7aa",
                          padding: 12,
                          background: "#fffaf5"
                        }}
                      >
                        <div
                          style={{
                            fontSize: 13,
                            color: "#7c2d12",
                            lineHeight: 1.45
                          }}
                        >
                          <strong>{a.title}</strong> — {a.message}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                            marginTop: 10
                          }}
                        >
                          <button
                            onClick={() => onAlertAction?.(a)}
                            style={{
                              ...ui.secondaryButton,
                              background: "#fff7ed",
                              border: "1px solid #fdba74",
                              color: "#9a3412"
                            }}
                          >
                            {a.actionType === "OPEN_FINANCE"
                              ? "Abrir finanzas"
                              : a.actionType === "OPEN_PLAN"
                                ? "Abrir plan"
                                : "Agendar"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              ) : null}

              {loadingPatientSnapshot ? (
                <div style={{ color: "#64748b", fontSize: 13 }}>Cargando expediente...</div>
              ) : !patientSnapshot ? (
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  Sin información clínica disponible.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 14 }}>
                  <SectionCard
                    title="Datos del paciente"
                    subtitle="Identificación rápida"
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                        gap: 10
                      }}
                    >
                      <InfoTile
                        label="Paciente"
                        value={`${patientSnapshot.patient?.firstName || ""} ${patientSnapshot.patient?.lastName || ""}`.trim() || "Paciente"}
                        strong
                      />
                      <InfoTile
                        label="Teléfono"
                        value={patientSnapshot.patient?.phone || "-"}
                      />
                      <InfoTile
                        label="Email"
                        value={patientSnapshot.patient?.email || "-"}
                      />
                      <InfoTile
                        label="Cita actual"
                        value={selectedAppointment?.reason || selectedAppointment?.treatmentName || "-"}
                      />
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="Resumen"
                    subtitle="Lectura ejecutiva del paciente"
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "repeat(2, minmax(0,1fr))"
                          : "repeat(5, minmax(0,1fr))",
                        gap: 10
                      }}
                    >
                      <MiniMetric
                        label="Citas recientes"
                        value={String(patientSnapshot.summary?.recentAppointmentsCount || 0)}
                      />
                      <MiniMetric
                        label="Próximas citas"
                        value={String(patientSnapshot.summary?.upcomingAppointmentsCount || 0)}
                      />
                      <MiniMetric
                        label="Tratamientos"
                        value={String(patientSnapshot.summary?.recentTreatmentsCount || 0)}
                      />
                      <MiniMetric
                        label="Odontograma"
                        value={String(patientSnapshot.summary?.odontogramUpdatesCount || 0)}
                      />
                      <MiniMetric
                        label="Pendiente"
                        value={formatMoney(patientSnapshot.summary?.pendingReceivables || 0)}
                        danger={Number(patientSnapshot.summary?.pendingReceivables || 0) > 0}
                      />
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="Planes de tratamiento"
                    subtitle="Estado actual y ejecución"
                  >
                    {loadingTreatmentPlans ? (
                      <div style={{ color: "#64748b", fontSize: 13 }}>Cargando planes...</div>
                    ) : !treatmentPlans.length ? (
                      <div style={{ color: "#64748b", fontSize: 13 }}>
                        Este paciente aún no tiene planes registrados.
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 10 }}>
                        {treatmentPlans.map((plan) => (
                          <div
                            key={plan.id}
                            style={{
                              ...ui.card,
                              padding: 12,
                              borderLeft: "4px solid #2563eb"
                            }}
                          >
                            <div style={{ fontWeight: 900, color: "#0f172a" }}>
                              {plan.title}
                            </div>

                            <div style={{ ...ui.metaText, marginTop: 4 }}>
                              Estado: {plan.status} · Creado por: {plan.createdByName || "Sin registro"}
                            </div>

                            <div style={{ ...ui.metaText, marginTop: 4 }}>
                              Estimado: {formatMoney(plan.totalEstimated)} · Producido: {formatMoney(plan.totalProduced)}
                            </div>

                            <div style={{ ...ui.metaText, marginTop: 4 }}>
                              Pendientes: {plan.pendingCount} · Realizados: {plan.doneCount}
                            </div>

                            {plan.notes && (
                              <div style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>
                                {plan.notes}
                              </div>
                            )}

                            {!!plan.items?.length && (
                              <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                                {plan.items.map((item) => (
                                  <div
                                    key={item.id}
                                    style={{
                                      ...ui.softCard,
                                      padding: 12
                                    }}
                                  >
                                    <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                      {item.treatmentName}
                                    </div>

                                    <div style={{ ...ui.metaText, marginTop: 4 }}>
                                      {item.tooth ? `Pieza ${item.tooth}` : "Sin pieza"}
                                      {item.surface ? ` · ${item.surface}` : ""}
                                      {item.condition ? ` · ${item.condition}` : ""}
                                    </div>

                                    <div style={{ ...ui.metaText, marginTop: 4, fontSize: 12 }}>
                                      Sugerido: {item.suggestedDoctorName || "-"} · Asignado: {item.assignedDoctorName || "-"}
                                    </div>

                                    <div style={{ ...ui.metaText, marginTop: 4, fontSize: 12 }}>
                                      Estado: {planItemStatusLabel(item.status)} · {formatMoney(item.price)}
                                    </div>

                                    {item.treatmentLogId ? (
                                      <div style={{ marginTop: 8, fontSize: 12, color: "#166534", fontWeight: 700 }}>
                                        Tratamiento realizado
                                        {item.appointmentStart ? ` · ${formatDateTime(item.appointmentStart)}` : ""}
                                      </div>
                                    ) : item.appointmentId ? (
                                      <div
                                        style={{
                                          marginTop: 8,
                                          fontSize: 12,
                                          color: item.appointmentStatus === "HELD" ? "#c2410c" : "#0f766e",
                                          fontWeight: 700
                                        }}
                                      >
                                        {item.appointmentStatus === "HELD"
                                          ? `Tratamiento con reserva temporal${item.appointmentStart ? ` · ${formatDateTime(item.appointmentStart)}` : ""}`
                                          : `Tratamiento con cita programada${item.appointmentStart ? ` · ${formatDateTime(item.appointmentStart)}` : ""}`}
                                        {item.appointmentDoctorName ? ` · ${item.appointmentDoctorName}` : ""}
                                      </div>
                                    ) : item.status !== "DONE" &&
                                      item.status !== "CANCELLED" &&
                                      !item.appointmentId ? (
                                      <button
                                        onClick={() => onScheduleFromPlanItem(item)}
                                        style={{
                                          ...ui.secondaryButton,
                                          marginTop: 8,
                                          background: "#dbeafe",
                                          border: "1px solid #bfdbfe",
                                          color: "#1d4ed8"
                                        }}
                                      >
                                        Agendar este ítem
                                      </button>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </SectionCard>

                  <SectionCard
                    title="Próximas citas"
                    subtitle="Seguimiento ya programado"
                  >
                    {patientSnapshot.upcomingAppointments?.length ? (
                      <div style={{ display: "grid", gap: 8 }}>
                        {patientSnapshot.upcomingAppointments.map((a) => (
                          <div
                            key={a.id}
                            style={{
                              ...ui.softCard,
                              padding: 12
                            }}
                          >
                            <div style={{ fontWeight: 800, color: "#0f172a" }}>
                              {formatDateTime(a.startTime)}
                            </div>
                            <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>
                              {a.reason || "Sin motivo"}
                            </div>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                              {a.clinicName || "-"} · {a.doctorName || "-"}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#64748b", fontSize: 13 }}>
                        No tiene próximas citas.
                      </div>
                    )}
                  </SectionCard>
                </div>
              )}
            </>
          )}

          {tab === "odontograma" && (
            <>
              {loadingPatientOdontogram ? (
                <div style={{ color: "#64748b", fontSize: 13 }}>Cargando odontograma...</div>
              ) : !patientOdontogram ? (
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  Sin información de odontograma disponible.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 14 }}>
                  <SectionCard
                    title="Paciente"
                    subtitle="Contexto del registro odontológico"
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                        gap: 10
                      }}
                    >
                      <InfoTile
                        label="Paciente"
                        value={`${patientOdontogram.patient?.firstName || ""} ${patientOdontogram.patient?.lastName || ""}`.trim() || "Paciente"}
                        strong
                      />
                      <InfoTile
                        label="Teléfono"
                        value={patientOdontogram.patient?.phone || "-"}
                      />
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="Nueva entrada en odontograma"
                    subtitle="Registro clínico rápido"
                  >
                    <div
                      style={{
                        ...ui.infoCard,
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        padding: 12,
                        marginBottom: 12
                      }}
                    >
                      <div style={{ fontWeight: 800, color: "#166534" }}>
                        Selecciona pieza, superficie, condición y estado clínico.
                      </div>
                    </div>

                    <label style={ui.label}>Diente</label>

                    <div
                      style={{
                        ...ui.softCard,
                        padding: 12,
                        marginBottom: 12
                      }}
                    >
                      <OdontogramSelector
                        selected={odontogramForm.tooth}
                        onSelect={(value) =>
                          setOdontogramForm((prev) => ({ ...prev, tooth: value }))
                        }
                      />
                    </div>

                    <div style={{ display: "grid", gap: 10 }}>
                      <select
                        value={odontogramForm.surface}
                        onChange={(e) =>
                          setOdontogramForm((prev) => ({ ...prev, surface: e.target.value }))
                        }
                        style={ui.select}
                      >
                        {surfaces.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>

                      <select
                        value={odontogramForm.condition}
                        onChange={(e) =>
                          setOdontogramForm((prev) => ({ ...prev, condition: e.target.value }))
                        }
                        style={ui.select}
                      >
                        {conditions.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>

                      <select
                        value={odontogramForm.status}
                        onChange={(e) =>
                          setOdontogramForm((prev) => ({ ...prev, status: e.target.value }))
                        }
                        style={ui.select}
                      >
                        {odontoStatuses.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>

                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button
                          onClick={onCreateOdontogramEntry}
                          disabled={creatingOdontogramEntry}
                          style={ui.primaryButton}
                        >
                          {creatingOdontogramEntry ? "Guardando..." : "Agregar entrada"}
                        </button>
                      </div>
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="Estado actual por pieza / superficie"
                    subtitle="Últimos registros del paciente"
                  >
                    {patientOdontogram.latestEntries?.length ? (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                          gap: 10
                        }}
                      >
                        {patientOdontogram.latestEntries.map((item) => (
                          <ToothCard
                            key={item.id}
                            item={item}
                            formatDateTime={formatDateTime}
                          />
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#64748b", fontSize: 13 }}>
                        Sin registros actuales en odontograma.
                      </div>
                    )}
                  </SectionCard>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div
      style={{
        ...ui.card,
        padding: 14
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 900,
            color: "#0f172a"
          }}
        >
          {title}
        </div>

        {subtitle ? (
          <div
            style={{
              fontSize: 12,
              color: "#64748b",
              marginTop: 3
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>

      {children}
    </div>
  )
}

function InfoTile({ label, value, strong = false }) {
  return (
    <div
      style={{
        ...ui.softCard,
        padding: 12
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: "#64748b",
          marginBottom: 5
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: strong ? 15 : 14,
          fontWeight: strong ? 900 : 800,
          color: "#0f172a",
          lineHeight: 1.35,
          wordBreak: "break-word"
        }}
      >
        {value}
      </div>
    </div>
  )
}

function MiniMetric({ label, value, danger = false }) {
  return (
    <div
      style={{
        border: `1px solid ${danger ? "#fecaca" : "#e2e8f0"}`,
        background: danger ? "#fef2f2" : "#ffffff",
        borderRadius: 12,
        padding: 12
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: "#64748b",
          marginBottom: 4
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 18,
          fontWeight: 900,
          color: danger ? "#991b1b" : "#0f172a"
        }}
      >
        {value}
      </div>
    </div>
  )
}

function ToothCard({ item, formatDateTime }) {
  return (
    <div
      style={{
        ...ui.card,
        padding: 12
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a" }}>
        Pieza {item.tooth}
        {item.surface ? ` · ${item.surface}` : ""}
      </div>

      <div style={{ fontSize: 13, color: "#475569", marginTop: 6 }}>
        {item.condition}
      </div>

      <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
        {item.status} · {formatDateTime(item.createdAt)}
      </div>
    </div>
  )
}